import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, map, pick, toNumber } from 'lodash';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { shouldRenderRunAction } from 'js/components/pipelines/cards/components/ExpandActions/utils/shouldRenderSourceAction';
import {
  ADD_DATA_SOURCE_REQUEST,
  GET_DATA_SOURCE_REQUEST,
  GET_FULL_VENDOR_REQUEST,
  UPDATE_SOURCE_INFO_REQUEST,
} from 'js/constants/actionTypes';
import { CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI } from 'js/constants/connectionCategories';
import { FORM_TYPE_CREATE_ELT } from 'js/constants/formTypes';
import { getCurrentProjectId } from 'js/reducers/app/selectors';
import { createResourceSetupService } from 'new/components/flows/configure/ELT/ELTSourceSetup/service/createResourceSetupService';
import { detectTemplateVersion } from 'new/components/flows/configure/ELT/ELTSourceSetup/utils/templateVersionDetector';
import { useChangeDetection } from 'new/components/flows/hooks/useChangeDetection';
import createFlowService from 'new/components/flows/service/createFlowService';
import useELT from 'new/components/flows/types/elt/CreateELTFlow/ELTFlow/useELT';
import flowTypes from 'new/components/flows/types/flowTypes';
import useFlowParams from 'new/components/flows/useFlowParams';
import { useForm } from 'new/elements/Form/useForm';
import { useDebugLogger } from 'new/hooks/useDebugLogger';
import { usePreviousPath } from 'new/hooks/usePreviousPath';
import { useDataSourceRepository } from 'new/state/data_source/repository/useDataSourceRepository';
import useFormsAdapter from 'new/state/forms/adapter/useFormsAdapter';
import { useHashedState } from 'new/state/hashedState/repository/useHashedState';
import usePaging from 'new/state/paging/repository/usePaging';
import useRequests from 'new/state/request/repository/useRequests';
import useSourcesAdapter from 'new/state/sources/adapter/useSourcesAdapter';
import useVendorsAdapter from 'new/state/vendors/adapter/useVendorsAdapter';
import { useVendors } from 'new/state/vendors/repository/useVendors';
import {
  assignFlowToProjectIfPresent,
  redirectToProjectIfNeeded,
} from 'new/utils/projectAssignment';
import { useELTSaveConfirmation } from '../hooks/useELTSaveConfirmation';
import { isUiModeCode } from 'new/utils/uiMode';
import { getDefaultDataEndpoint } from 'new/utils/actionEndpoints';

export default function useSourceSetup() {
  const debugLogger = useDebugLogger();
  const dispatch = useAppDispatch();
  const currentProjectId = useSelector(getCurrentProjectId);
  const {
    selectedConnection,
    credentialId,
    sourceConfig,
    name,
    description,
    data,
    selectedFeeds,
    updateFormData,
    connectionCategory,
  } = useELT();

  const history = useHistory();
  const { id, isModal, onDismiss } = useFlowParams();
  const flowService = createFlowService();
  const previousPath = usePreviousPath();
  const { getDataSourceById } = useDataSourceRepository();
  const { save: saveHashedState } = useHashedState('flows.selected');

  const { createSource, updateSource } = useFormsAdapter(flowTypes.elt);
  const { requestFullVendor } = useVendorsAdapter();
  const { getSourceById } = useSourcesAdapter();
  const sourceBeingEditedId = toNumber(id);
  const { overrideNextFunction, setLoaderMessages } = useForm();
  const { vendors, vendorEndpoints } = useVendors();
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);

  const currentRunWithIngestionRef = useRef<boolean>(false);

  // Save confirmation logic
  const {
    showSaveConfirmation,
    setShowSaveConfirmation,
    isSaving,
    setIsSaving,
    pendingSaveRef,
    currentRunWithIngestion,
    hasChanges,
    shouldShowSaveConfirmation,
    handleSaveConfirmation,
    onCloseSaveConfirmation,
    setShowLeaveConfirmation,
    overrideNextFunction: overrideFormNextFunction,
  } = useELTSaveConfirmation();

  // Get change detection at component level
  const { hasChanges: currentHasChanges } = useChangeDetection(FORM_TYPE_CREATE_ELT);

  const { showError } = useRequests();
  const { getSourceConfigFields, getMetaInfoById, paging } = usePaging();

  const service = createResourceSetupService();

  const sourceFields = getSourceConfigFields(selectedConnection);

  const dataSourceRef = useRef<any>(getDataSourceById(sourceBeingEditedId));

  useEffect(() => {
    dataSourceRef.current = getDataSourceById(sourceBeingEditedId);
  }, [sourceBeingEditedId, getDataSourceById]);

  // Template version detection - single source of truth
  const templateVersionInfo = useMemo(() => {
    // If still loading initial data, don't try to detect template version yet
    if (isLoadingInitialData) {
      debugLogger.debugLog(1, 'useSourceSetup: Template version info: Still loading initial data');
      return {
        templateVersion: 'v1' as const,
        isV2Template: false,
        isLoading: true,
      };
    }

    if (!selectedConnection) {
      debugLogger.debugLog(1, 'useSourceSetup: Template version info: No selected connection');
      return {
        templateVersion: 'v1' as const,
        isV2Template: false,
        isLoading: false,
      };
    }

    // Get the complete vendor object for this connection
    const { vendorId } = getMetaInfoById(selectedConnection);
    const vendor = vendorId ? vendors[vendorId] : null;

    if (!vendor) {
      debugLogger.debugLog(
        1,
        `useSourceSetup: Template version info: No vendor found for ${selectedConnection} '- vendor may still be loading`,
      );
      return {
        templateVersion: 'v1' as const,
        isV2Template: false,
        isLoading: true,
      };
    }

    const templateVersion = detectTemplateVersion(vendor);

    const isV2Template = templateVersion === 'v2';
    debugLogger.debugLog(1, 'useSourceSetup: Template version info:', {
      templateVersion,
      isV2Template,
    });

    return {
      templateVersion,
      isV2Template,
      isLoading: false,
    };
  }, [selectedConnection, vendors, isLoadingInitialData, getMetaInfoById]);

  // Store template version in Redux for other hooks to consume
  // Only update when template version actually changes
  const previousTemplateVersionInfoRef = useRef<typeof templateVersionInfo | null>(null);

  useEffect(() => {
    if (selectedConnection) {
      const hasChanged =
        !previousTemplateVersionInfoRef.current ||
        previousTemplateVersionInfoRef.current.templateVersion !==
          templateVersionInfo.templateVersion ||
        previousTemplateVersionInfoRef.current.isV2Template !== templateVersionInfo.isV2Template;

      if (hasChanged) {
        previousTemplateVersionInfoRef.current = templateVersionInfo;
        updateFormData({
          templateVersionInfo: {
            templateVersion: templateVersionInfo.templateVersion,
            isV2Template: templateVersionInfo.isV2Template,
            selectedConnection,
          },
        });
      }
    }
  }, [selectedConnection, templateVersionInfo.templateVersion, templateVersionInfo.isV2Template]);

  const validate = useCallback(() => {
    if (!name) {
      showError('Please set a name for this source.');
      return false;
    }

    if (!isUiModeCode(sourceConfig)) {
      const missingFields = service.getMissingFields(
        sourceFields,
        sourceConfig,
        templateVersionInfo,
      );

      if (!isEmpty(missingFields)) {
        const errorMessage =
          'Please enter the following required fields: ' +
          map(missingFields, 'display_name').join(', ') +
          '.';

        showError(errorMessage);

        return false;
      }
    }

    if (isEmpty(selectedFeeds)) {
      showError('Please select at least one feed.');
      return false;
    }

    return true;
  }, [service, sourceFields, sourceConfig, name, showError, selectedFeeds, templateVersionInfo]);

  // Update the ref when data changes
  useEffect(() => {
    currentRunWithIngestionRef.current = data.runWithIngestion || false;
  }, [data.runWithIngestion]);

  const performSave = useCallback(
    async (runWithIngestion: boolean) => {
      if (!validate()) {
        return false;
      }

      // Update form data with runWithIngestion
      updateFormData({ runWithIngestion });

      // Get vendorFeedSpecs from form state
      const vendorFeedSpecs = data.vendorFeedSpecs || null;

      // create/update source
      const sourceInfo = service.buildSourceInfo({
        selectedFeeds,
        sourceConfig,
        name,
        description: description || '',
        originalFeeds: data.originalFeeds || {},
        availableFeeds: data.discoveryResults?.availableFeeds || [],
        vendorFeedSpecs,
        templateVersionInfo,
      });

      if (sourceBeingEditedId) {
        sourceInfo.id = sourceBeingEditedId;
      }

      try {
        const response = sourceBeingEditedId
          ? await updateSource({
              connectionCategory: CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
              sourceInfo,
              credentialId,
              runWithIngestion: runWithIngestion,
            })
          : await createSource({
              connectionCategory: CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
              sourceInfo,
              credentialId,
              sourceType: 'api_multi',
            });

        const source = response.payload.formData.source;

        // Only navigate to edit screen when CREATING a new source (not updating)
        if (!sourceBeingEditedId && (source.origin_node_id || source.flow_node_id)) {
          try {
            const flowNodeId = source.origin_node_id || source.flow_node_id;
            const assignedProjectId = await assignFlowToProjectIfPresent({
              dispatch,
              flowNodeId,
              currentProjectId,
            });
            if (assignedProjectId) {
              redirectToProjectIfNeeded(history, assignedProjectId, dispatch);
              return;
            }
          } catch (error) {
            console.error('Failed to assign flow to project:', error);
          }

          const flowOriginId = source.origin_node_id || null;
          history.replace(
            flowService.getPathToGoAfterCreation(previousPath, undefined, {
              resourceType: 'source',
              flowType: 'elt',
              flowOriginId,
            }),
          );
          return;
        }

        if (source?.id) {
          if (isModal) {
            onDismiss?.({ addedDataSourceId: source.id, ...source });
            return;
          }
          if (id) {
            // Use setTimeout to ensure Redux state updates before navigation
            setTimeout(() => {
              history.goBack();
            }, 10);
          } else {
            // Save flow_node_id to hashedState for flow expansion
            if (source.flow_node_id) {
              saveHashedState(source.flow_node_id);
            }
            // Use setTimeout to ensure Redux state updates before navigation
            setTimeout(() => {
              history.replace(
                flowService.getPathToGoAfterCreation(previousPath, undefined, {
                  resourceType: 'source',
                  flowType: 'elt',
                  flowOriginId: source.flow_node_id,
                }),
              );
            }, 10);
          }
        }

        return true;
      } catch (e) {
        showError('Error creating source');
        return false;
      }
    },
    [
      validate,
      service,
      selectedFeeds,
      sourceConfig,
      name,
      sourceBeingEditedId,
      credentialId,
      history,
      showError,
      updateSource,
      createSource,
      data,
      updateFormData,
    ],
  );

  useEffect(() => {
    setLoaderMessages({
      [UPDATE_SOURCE_INFO_REQUEST]: 'Updating source info',
      [GET_DATA_SOURCE_REQUEST]: 'Loading source',
      [ADD_DATA_SOURCE_REQUEST]: 'Creating source',
      [GET_FULL_VENDOR_REQUEST]: 'Loading connector info',
    });
  }, []);

  useEffect(() => {
    if (sourceBeingEditedId) {
      initializeEditForm();
    } else {
      initializeCreateForm();
    }
  }, []);

  const initializeCreateForm = async () => {
    // set default vendor_endpoint_id
    setIsLoadingInitialData(true);

    // Fetch the vendor data for template version detection
    if (selectedConnection) {
      await requestFullVendor(selectedConnection);
    }

    const { vendorId } = getMetaInfoById(selectedConnection);
    // ELT is an ETL source: never default to an action endpoint
    const defaultEndpoint = getDefaultDataEndpoint(
      Object.values(pick(vendorEndpoints, vendors?.[vendorId]?.vendor_endpoints)) as any[],
    );

    if (defaultEndpoint) {
      updateFormData({
        sourceConfig: {
          ...sourceConfig,
          vendor_endpoint_id: defaultEndpoint.id,
          'ui.flow_type': 'elt', // This will be overridden by the flowType prop in ResourceParamFields
        },
      });
    }
    setIsLoadingInitialData(false);
  };

  const initializeEditForm = async () => {
    setIsLoadingInitialData(true);
    const {
      entities: { sources, vendors, credentials },
      result,
    } = await getSourceById(sourceBeingEditedId, true);
    const source = sources[result];
    let vendor;
    if (source.vendor) {
      vendor = vendors[source.vendor];
      await requestFullVendor(vendor.name);
    }

    const newFormData = {
      name: source.name,
      description: source?.description || '',
      selectedConnection: vendor?.name || source?.connection_type,
      credentialId: source.data_credentials,
      selectedCredential: credentials[source.data_credentials],
      sourceConfig: {
        ...(source.template_config?.params || {}),
        vendor_endpoint_id: source.vendor_endpoint,
        'start.cron': source.template_config?.['start.cron'],
      },
      selectedFeeds: service.buildSelectFeeds(source, sourceFields),
      // Store original feeds data for merging
      originalFeeds: source.template_config?.feeds_config?.feeds || {},
    };

    updateFormData(newFormData);
    setIsLoadingInitialData(false);
  };

  useEffect(() => {
    const onNext = async () => {
      if (!validate()) {
        return false;
      }

      // Check if we should show save confirmation

      // For ELT flows, we need to check if we have a source being edited
      const source = sourceBeingEditedId
        ? {
            ...dataSourceRef.current,
            id: sourceBeingEditedId,
            status: 'ACTIVE',
          }
        : null;

      const isSourceActive = source?.status === 'ACTIVE';
      const supportsRunAction = shouldRenderRunAction(source, connectionCategory);

      const shouldDisplaySaveConfirmation = !!(
        isSourceActive &&
        supportsRunAction &&
        currentHasChanges && // Only show confirmation if there are actual changes
        !isSaving
      );

      if (shouldDisplaySaveConfirmation) {
        setShowSaveConfirmation(true);
        return new Promise<boolean>((resolve) => {
          pendingSaveRef.current = async (runWithIngestion: boolean) => {
            const result = await performSave(runWithIngestion);
            if (result) {
              setShowLeaveConfirmation(false);
            }
            resolve(result);
            return result;
          };
        });
      } else {
        const result = await performSave(currentRunWithIngestion);
        if (result) {
          setShowLeaveConfirmation(false);
        }
        return result;
      }
    };

    overrideNextFunction(onNext);

    // Cleanup: Reset override function when dependencies change
    return () => {
      overrideNextFunction(null);
    };
  }, [
    performSave,
    currentRunWithIngestion,
    currentHasChanges,
    isSaving,
    validate,
    setShowSaveConfirmation,
    pendingSaveRef,
    setShowLeaveConfirmation,
    sourceBeingEditedId,
    connectionCategory,
    overrideNextFunction,
  ]);

  const onNameChanged = (name) => {
    updateFormData({
      name,
    });
  };

  const onDescriptionChanged = (description) => {
    updateFormData({
      description,
    });
  };

  return {
    onNameChanged,
    onDescriptionChanged,
    isLoadingInitialData,
    performSave,
    isEditMode: !!sourceBeingEditedId,
    // Template version information for debugging/logging
    templateVersionInfo,
    // Save confirmation state
    showSaveConfirmation,
    setShowSaveConfirmation,
    isSaving,
    setIsSaving,
    pendingSaveRef,
    currentRunWithIngestion,
    hasChanges,
    shouldShowSaveConfirmation,
    handleSaveConfirmation,
    onCloseSaveConfirmation,
    setShowLeaveConfirmation,
    overrideFormNextFunction,
  };
}
